/**
 * Shared building blocks of ④ 创建 and ⑤ 开启 (Paper TP-10 … TP-15a): the progress
 * card, the fixed-height campaign list and its rows.
 */
import type { TemplatePortfolioExecutionItem } from "@adpilot/shared";
import { IconRefresh } from "@tabler/icons-react";
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { i18n } from "@/i18n";
import { cn } from "@/lib/utils";

export type ExecutionTone = "success" | "destructive" | "brand" | "info" | "track" | "muted";

const DOT: Record<ExecutionTone, string> = {
  success: "bg-success",
  destructive: "bg-destructive",
  brand: "bg-primary",
  info: "bg-info",
  track: "bg-line",
  muted: "bg-muted-foreground",
};

/** ④ uses the 14px card radius, ⑤ the 10px one (Paper). */
type Variant = "execute" | "enable";

export interface ProgressSegment {
  tone: ExecutionTone;
  count: number;
  label: string;
}

export function ExecutionProgressCard({
  variant,
  caption,
  value,
  unit,
  legend,
  bar,
  barTrack = false,
  children,
}: {
  variant: Variant;
  caption: string;
  value: string;
  unit: string;
  legend: ProgressSegment[];
  /** Omitted on ⑤ 待开启, which has no bar. */
  bar?: ProgressSegment[];
  /** ⑤ draws the unfilled part on a subtle track; ④ fills it with the 等待 segment. */
  barTrack?: boolean;
  children?: React.ReactNode;
}) {
  return (
    <section
      className={cn(
        "flex flex-col gap-4 border border-line bg-surface p-5",
        variant === "execute" ? "rounded-lg" : "rounded-md",
      )}
    >
      <div className="flex items-end justify-between gap-6">
        <div className="flex flex-col gap-1">
          <p className="text-xs text-muted-foreground">{caption}</p>
          <p className="flex items-baseline gap-2">
            <span className="font-bold text-[32px] leading-10 tracking-[-0.02em] text-ink tabular-nums">
              {value}
            </span>
            <span
              className={cn(
                "text-text-secondary",
                variant === "execute" ? "text-[13px] leading-[18px]" : "text-sm leading-5",
              )}
            >
              {unit}
            </span>
          </p>
        </div>
        <ul className="flex items-center gap-4">
          {legend.map((segment) => (
            <li key={segment.label} className="flex items-center gap-1.5">
              <span className={cn("size-2 shrink-0 rounded-full", DOT[segment.tone])} />
              <span className="text-xs text-text-secondary tabular-nums">
                {segment.label} {segment.count}
              </span>
            </li>
          ))}
        </ul>
      </div>
      {bar ? (
        <div
          aria-hidden
          className={cn(
            "flex h-2 shrink-0 gap-0.5 overflow-clip rounded-full",
            barTrack && "bg-subtle",
          )}
        >
          {bar
            .filter((segment) => segment.count > 0)
            .map((segment) => (
              <span
                key={segment.label}
                className={cn("basis-0", segment.tone !== "track" && DOT[segment.tone])}
                style={{ flexGrow: segment.count }}
              />
            ))}
        </div>
      ) : null}
      {children ? <div className="flex flex-wrap items-center gap-2">{children}</div> : null}
    </section>
  );
}

/** Fixed-height list: the header stays put while the rows scroll inside. */
export function ExecutionList({
  variant,
  detailHeader,
  children,
}: {
  variant: Variant;
  detailHeader: string;
  children: React.ReactNode;
}) {
  const { t } = useTranslation("templatePortfolio");
  return (
    <section
      className={cn(
        "flex shrink-0 flex-col overflow-clip border border-line bg-surface",
        variant === "execute" ? "h-[410px] rounded-lg" : "h-[470px] rounded-md",
      )}
    >
      <div className="flex h-9 shrink-0 items-center gap-3 border-b border-line bg-table-footer px-4 text-xs font-medium text-muted-foreground">
        <span className="w-4 shrink-0" />
        <span className="min-w-0 flex-1">{t("execute.columns.campaign")}</span>
        <span className="w-[300px] shrink-0">{detailHeader}</span>
        <span className="w-[120px] shrink-0">{t("execute.columns.status")}</span>
      </div>
      <ul className="paper-scrollbar min-h-0 flex-1 overflow-y-auto">{children}</ul>
    </section>
  );
}

export function ExecutionRow({
  variant,
  icon,
  name,
  muted = false,
  detail,
  detailClassName = "text-muted-foreground",
  status,
  className,
}: {
  variant: Variant;
  icon: React.ReactNode;
  name: string;
  /** 等待 / 已跳过 rows dim the name. */
  muted?: boolean;
  detail: React.ReactNode;
  detailClassName?: string;
  status: React.ReactNode;
  className?: string;
}) {
  return (
    <li
      className={cn(
        "flex h-11 shrink-0 items-center gap-3 border-b border-line px-4 [&>svg]:size-4 [&>svg]:shrink-0",
        className,
      )}
    >
      {icon}
      <span
        title={name}
        className={cn(
          "min-w-0 flex-1 truncate leading-[18px]",
          variant === "execute" ? "text-[13px]" : "text-sm",
          muted ? "text-text-secondary" : "text-ink",
        )}
      >
        {name}
      </span>
      <span className={cn("w-[300px] shrink-0 truncate text-xs", detailClassName)}>{detail}</span>
      <span className="flex w-[120px] shrink-0">{status}</span>
    </li>
  );
}

const BADGE: Record<"success" | "info" | "neutral" | "paused", string> = {
  success: "bg-badge-success-background text-success-foreground",
  info: "bg-badge-information-background text-badge-information-foreground",
  neutral: "bg-subtle text-muted-foreground",
  paused: "bg-subtle text-text-secondary",
};

export function ExecutionBadge({
  variant,
  tone,
  children,
}: {
  variant: Variant;
  tone: keyof typeof BADGE;
  children: React.ReactNode;
}) {
  return (
    <span
      className={cn(
        "inline-flex h-5 items-center px-1.5 font-medium text-[11px] leading-[14px]",
        variant === "execute" ? "rounded-[6px]" : "rounded-[4px]",
        BADGE[tone],
      )}
    >
      {children}
    </span>
  );
}

export function RowRetryButton({ onClick, disabled }: { onClick: () => void; disabled?: boolean }) {
  const { t } = useTranslation("templatePortfolio");
  return (
    <Button
      variant="destructive-outline"
      size="xs"
      onClick={onClick}
      disabled={disabled}
      className="h-7 gap-1.5 rounded-[6px] border-badge-destructive-border bg-surface px-2.5"
    >
      <IconRefresh className="size-3.5" stroke={1.8} aria-hidden />
      {t("execute.retry")}
    </Button>
  );
}

/** Row icon for an item in progress: a brand arc turning on a grey track (TP-10). */
export function RowSpinner() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      strokeLinecap="round"
      className="size-4 shrink-0 animate-spin"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="9" className="stroke-line" strokeWidth="2.2" />
      <path d="M12 3a9 9 0 0 1 9 9" className="stroke-primary" strokeWidth="2.2" />
    </svg>
  );
}

/** Spinner on the disabled 创建中 footer button: white arc on a faint white ring. */
export function ButtonSpinner() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      strokeLinecap="round"
      className="size-3.5 shrink-0 animate-spin opacity-100"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="9" stroke="rgb(255 255 255 / 35%)" strokeWidth="2.6" />
      <path d="M12 3a9 9 0 0 1 9 9" stroke="#fff" strokeWidth="2.6" />
    </svg>
  );
}

/** Failed / skipped items first; otherwise the plan's order. */
export function pinFirst<T extends { ordinal: number }>(items: T[], pinned: (item: T) => boolean) {
  return [...items].sort((a, b) => Number(pinned(b)) - Number(pinned(a)) || a.ordinal - b.ordinal);
}

/** 类型 column of ⑤ (Paper TP-13): 核心词 · 广泛, 长尾词, 自动, 商品投放, 捡漏 … */
export function campaignTypeLabel(
  t: TFunction<"templatePortfolio">,
  item: Pick<TemplatePortfolioExecutionItem, "layer" | "campaignKey">,
): string {
  const label = t(`enable.layers.${item.layer}`);
  if (item.layer !== "core") return label;
  // Core keys are `core:{keyword}:{matchType}`.
  const matchType = (item.campaignKey.split(":").at(-1) ?? "").toLowerCase();
  return i18n.exists(`templatePortfolio:enable.matchTypes.${matchType}`)
    ? `${label} · ${t(`enable.matchTypes.${matchType}` as "enable.matchTypes.broad")}`
    : label;
}

/** A failure reason the user can read: the localized error code, if we have one. */
export function failureReason(code: string | null): string | null {
  const key = `errors:${code}`;
  return code && i18n.exists(key) ? i18n.t(key) : null;
}
